import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  StatusFunnel,
  ProcessingTimeData,
  VendorAnalysis,
} from '@/hooks/finance/useCashOutflowReport';

const COLORS = ['#6366f1', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];

const STATUS_COLORS: Record<string, string> = {
  'Draft': '#6366f1',
  'Pending Approval': '#f59e0b',
  'Approved': '#22c55e',
  'Paid': '#0088FE',
  'Rejected': '#ef4444',
  'Cancelled': '#9ca3af',
};

interface PaymentRequestPerformanceProps {
  statusFunnel: StatusFunnel[];
  processingTimeData: ProcessingTimeData[];
  vendorAnalysis: VendorAnalysis[];
}

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-ET', {
    style: 'currency',
    currency: 'ETB',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

export const PaymentRequestPerformance: React.FC<PaymentRequestPerformanceProps> = ({
  statusFunnel,
  processingTimeData,
  vendorAnalysis,
}) => {
  return (
    <div className="space-y-6">
      {/* Status Funnel */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Payment Request Status Distribution</CardTitle>
        </CardHeader>
        <CardContent>
          {statusFunnel.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={statusFunnel}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis 
                  dataKey="status" 
                  tick={{ fontSize: 12 }}
                />
                <YAxis 
                  yAxisId="left"
                  tick={{ fontSize: 12 }}
                  label={{ value: 'Count', angle: -90, position: 'insideLeft' }}
                />
                <YAxis 
                  yAxisId="right"
                  orientation="right"
                  tick={{ fontSize: 12 }}
                  tickFormatter={(v) => `${(v / 1000).toFixed(0)}K`}
                  label={{ value: 'Value (ETB)', angle: 90, position: 'insideRight' }}
                />
                <Tooltip 
                  formatter={(value: number, name: string) => [
                    name === 'value' ? formatCurrency(value) : value,
                    name === 'value' ? 'Total Value' : 'Count'
                  ]}
                  contentStyle={{ 
                    backgroundColor: 'hsl(var(--background))', 
                    border: '1px solid hsl(var(--border))' 
                  }}
                />
                <Legend />
                <Bar 
                  yAxisId="left" 
                  dataKey="count" 
                  fill="#8884d8" 
                  name="Count"
                  radius={[4, 4, 0, 0]}
                >
                  {statusFunnel.map((entry) => (
                    <Cell 
                      key={`cell-${entry.status}`} 
                      fill={STATUS_COLORS[entry.status] || '#8884d8'} 
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[300px] flex items-center justify-center text-muted-foreground">
              No status data available
            </div>
          )}
        </CardContent>
      </Card>

      {/* Processing Time Analysis */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Processing Time by Department (Days)</CardTitle>
        </CardHeader>
        <CardContent>
          {processingTimeData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={processingTimeData}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis 
                  dataKey="department" 
                  tick={{ fontSize: 11 }}
                  interval={0}
                  angle={-15}
                  textAnchor="end"
                  height={60}
                />
                <YAxis 
                  tick={{ fontSize: 12 }}
                  label={{ value: 'Days', angle: -90, position: 'insideLeft' }}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'hsl(var(--background))', 
                    border: '1px solid hsl(var(--border))' 
                  }}
                />
                <Legend />
                <Bar 
                  dataKey="avgDaysToApprove" 
                  fill="#f59e0b" 
                  name="To Approve"
                  radius={[4, 4, 0, 0]}
                />
                <Bar 
                  dataKey="avgDaysToPay" 
                  fill="#22c55e" 
                  name="To Pay"
                  radius={[4, 4, 0, 0]}
                />
                <Bar 
                  dataKey="avgTotalDays" 
                  fill="#6366f1" 
                  name="Total"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[300px] flex items-center justify-center text-muted-foreground">
              No processing time data available
            </div>
          )}
        </CardContent>
      </Card>

      {/* Vendor Analysis */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Top Vendors by Payment Volume</CardTitle>
        </CardHeader>
        <CardContent>
          {vendorAnalysis.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Vendor</TableHead>
                  <TableHead className="text-right">Total Amount</TableHead>
                  <TableHead className="text-right">Transactions</TableHead>
                  <TableHead className="text-right">Avg Amount</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {vendorAnalysis.map((vendor, index) => (
                  <TableRow key={vendor.vendorName}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <span 
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: COLORS[index % COLORS.length] }}
                        />
                        {vendor.vendorName}
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {formatCurrency(vendor.totalAmount)}
                    </TableCell>
                    <TableCell className="text-right">
                      {vendor.transactionCount}
                    </TableCell>
                    <TableCell className="text-right text-muted-foreground">
                      {formatCurrency(vendor.avgAmount)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="h-[200px] flex items-center justify-center text-muted-foreground">
              No vendor data available
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
